import React from "react";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Star } from "lucide-react";

const LAW_TITLES = ["Attorney", "Paralegal", "Legal Assistant", "Office Manager", "Other"];
export const TITLES_BY_TYPE = {
  "Law Firm": LAW_TITLES,
  "Attorney": LAW_TITLES,
  "Process Server": ["Owner", "Process Server", "Dispatcher", "Office Manager", "Other"],
  "Wholesaler": ["Account Manager", "Owner", "Dispatcher", "Office Manager", "Other"],
  "Government": ["Clerk", "Attorney", "Case Manager", "Officer", "Other"],
  "Collection Agency": ["Account Manager", "Collector", "Attorney", "Office Manager", "Other"],
  "Individual": ["Pro-Se Client", "Family Member", "Other"],
  "Other": ["Contact", "Owner", "Manager", "Other"],
};
export const CONTACT_TITLES = LAW_TITLES;
export const titlesFor = (companyType) => TITLES_BY_TYPE[companyType] || TITLES_BY_TYPE.Other;
export const isLawType = (companyType) => companyType === "Law Firm" || companyType === "Attorney";
const SEL = "h-9 rounded-md border border-slate-200 bg-white px-2 text-xs w-full";
const newId = () => `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export function contactLabel(c, all = []) {
  const boss = c.reports_to ? all.find((x) => x.id === c.reports_to) : null;
  return `${c.name}${c.title ? ` — ${c.title}` : ""}${boss ? ` (for ${boss.name})` : ""}`;
}

export function ContactsEditor({ contacts, onChange, readOnly, companyType = "Law Firm" }) {
  const titles = titlesFor(companyType);
  const law = isLawType(companyType);
  const upd = (i, k, v) => onChange(contacts.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const attorneys = contacts.filter((c) => c.title === "Attorney" && c.name);
  const defaultTitle = () => (law ? (contacts.length ? "Paralegal" : "Attorney") : titles[0]);
  return (
    <div className="space-y-2" data-testid="contacts-editor">
      {contacts.length === 0 && <div className="text-xs text-slate-400">No contacts yet.</div>}
      {contacts.map((c, i) => (
        <div key={c.id} className="rounded border border-slate-200 bg-slate-50 p-2 space-y-2" data-testid={`contact-row-${i}`}>
          <div className="grid grid-cols-[1fr_150px_auto] gap-2 items-center">
            <Input className="h-9 text-sm bg-white" placeholder="Full name" disabled={readOnly} value={c.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`contact-name-${i}`} />
            <select className={SEL} disabled={readOnly} value={c.title} onChange={(e) => upd(i, "title", e.target.value)} data-testid={`contact-title-${i}`}>
              {[...titles, ...(c.title && !titles.includes(c.title) ? [c.title] : [])].map((t) => <option key={t}>{t}</option>)}
            </select>
            <div className="flex items-center gap-1">
              <button type="button" title={c.primary ? "Primary contact" : "Make primary"} disabled={readOnly} data-testid={`contact-primary-${i}`}
                onClick={() => onChange(contacts.map((x, j) => ({ ...x, primary: j === i })))} className={c.primary ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}><Star className="h-4 w-4" fill={c.primary ? "currentColor" : "none"} /></button>
              {!readOnly && <button type="button" className="text-slate-400 hover:text-rose-600" data-testid={`contact-remove-${i}`} onClick={() => onChange(contacts.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Input className="h-9 text-sm bg-white" type="email" placeholder="Email" disabled={readOnly} value={c.email} onChange={(e) => upd(i, "email", e.target.value)} data-testid={`contact-email-${i}`} />
            <Input className="h-9 text-sm bg-white" placeholder="Phone" disabled={readOnly} value={c.phone} onChange={(e) => upd(i, "phone", e.target.value)} data-testid={`contact-phone-${i}`} />
            {law && c.title !== "Attorney" && (
              <select className={SEL} disabled={readOnly} value={c.reports_to || ""} onChange={(e) => upd(i, "reports_to", e.target.value || null)} data-testid={`contact-reports-to-${i}`}>
                <option value="">Works for (attorney)…</option>
                {attorneys.filter((a) => a.id !== c.id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>
        </div>
      ))}
      {!readOnly && (
        <button type="button" className="sm-link text-xs inline-flex items-center gap-1" data-testid="contact-add"
          onClick={() => onChange([...contacts, { id: newId(), name: "", title: defaultTitle(), email: "", phone: "", reports_to: null, primary: contacts.length === 0 }])}>
          <Plus className="h-3 w-3" />Add contact
        </button>
      )}
    </div>
  );
}
